import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import { fetchWithRefresh } from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import SearchIcon from "/public/images/icons/search.svg?react";
import TextInput from "../../components/TextInput";
import DatePicker from "../../components/DatePicker";
import BookingCard from "../../components/BookingCard/Staff";
import Button from "../../components/Button";

// eslint-disable-next-line no-unused-vars


export default function StaffBookings() {
  const navigate = useNavigate();
  const { setTitle, setBackButton, setFilterFunc } = useHeaderContext();
  const { user, logout, updateTokens, selectedVenue: selectedOrg } = useUserContext();
  const [filterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [filterTerm] = useState("");
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedActivity, setSelectedActivity] = useState([]);
  const [selectedVenue] = useState([]);
  const [venueFilters, setVenueFilters] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);

  const filterBookings = useCallback(() => {
    let filtered = bookings;

    if (filterTerm) {
      filtered = filtered.filter((booking) =>
        booking.BookableThingName?.toLowerCase().includes(filterTerm.toLowerCase())
      );
    }

    if (selectedVenue.length > 0) {
      filtered = filtered.filter((booking) =>
        selectedVenue.includes(booking.BookableThingName)
      );
    }

    if (dateFrom) {
      filtered = filtered.filter((booking) =>
        booking.DateSlot && booking.DateSlot.slice(0, 10) >= dateFrom
      );
    }

    if (dateTo) {
      filtered = filtered.filter((booking) =>
        booking.DateSlot && booking.DateSlot.slice(0, 10) <= dateTo
      );
    }

    if (selectedActivity.length > 0) {
      filtered = filtered.filter((booking) =>
        selectedActivity.includes(booking.BaseActivityId)
      );
    }

    setFilteredBookings(filtered);
  }, [filterTerm, selectedVenue, dateFrom, dateTo, selectedActivity]);

  const searchBookings = useCallback((term) => {
    setSearchTerm(term);
    const lower = term.toLowerCase();
    if (term) {
      setFilteredBookings(
        bookings.filter((booking) =>
          booking.BaseActivityName?.toLowerCase().includes(lower) ||
          booking.BookableThingName?.toLowerCase().includes(lower) ||
          booking.LeadParticipant?.toLowerCase().includes(lower)
        )
      );
    } else {
      setFilteredBookings(bookings);
    }
  }, [bookings]);

  useEffect(() => {
    setTitle("All Bookings " + `(${bookings.length})`);
    setBackButton(true);
    setFilterFunc(toggleFilterOverlay);
  }, [filterOverlayVisible, bookings.length]);

  useEffect(() => {
    if (!selectedOrg?.OrgID) return;
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const url = `${import.meta.env.VITE_API_URL}bookings/venuecurrentbookings?orgid=${selectedOrg.OrgID}`;
        //const url = `${import.meta.env.VITE_API_URL}bookings/venuecurrentbookings?orgid=11`;
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });

        if (!response.ok) {
          toast.error("Failed to load bookings.");
          return;
        }

        const data = await response.json();
        const bookingsArray = Array.isArray(data) ? data : (data.bookings ?? data.data ?? data.$values ?? []);
        const visible = bookingsArray.filter((b) => (b.status ?? b.Status) !== -1);
        const sorted = [...visible].sort((a, b) => (a.DateSlot ?? "") > (b.DateSlot ?? "") ? -1 : 1);
        setBookings(sorted);
        setFilteredBookings(sorted);
        const seen = new Set();
        setActivityTypes(
          sorted
            .filter((b) => b.BaseActivityId && !seen.has(b.BaseActivityId) && seen.add(b.BaseActivityId))
            .map((b) => ({ BaseActivityId: b.BaseActivityId, BaseActivityName: b.BaseActivityName }))
        );
      } catch (error) {
        toast.error("Failed to load bookings.");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [selectedOrg?.OrgID]);

  useEffect(() => {
    filterBookings();
  }, [selectedVenue, filterBookings]);

  const toggleFilterOverlay = () => {
    setFilterOverlayVisible((prevState) => !prevState);
  };

  const handleActivityClick = (activity) => {
    setSelectedActivity((prevSelected) => {
      if (prevSelected.includes(activity)) {
        return prevSelected.filter((item) => item !== activity);
      } else {
        return [...prevSelected, activity];
      }
    });
  };

  // const handleVenueClick = (venue) => {
  //   setSelectedVenue((prevSelected) => {
  //     if (prevSelected.includes(venue)) {
  //       return prevSelected.filter((item) => item !== venue);
  //     } else {
  //       return [...prevSelected, venue];
  //     }
  //   });
  // };

  const groupBookingsByDate = (bookings) => {
    return bookings.reduce((acc, booking) => {
      const date = booking.DateSlot?.split('T')[0];
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(booking);
      return acc;
    }, {});
  };

  const groupedBookings = groupBookingsByDate(filteredBookings);
  const sortedDates = Object.keys(groupedBookings).sort().reverse();

  return (
    <div className="relative bg-sportsLightGrey min-h-screen p-[24px] pb-28">
      <p className="font-semibold tracking-widest text-[#101820] text-[12px]">Venue</p>
      <p className="font-semibold text-[24px]">{selectedOrg?.OrgPartnerFriendlyName}</p>
      <div>
        <Button variant="primary" className="w-full mt-4" onClick={() => navigate('/bookings/scan')}>
          <img src="/images/icons/normalScan.svg" className="w-10 h-10" />
          Scan Booking Code
        </Button>
      </div>
      <div className="relative w-full mb-[30px] mt-[30px]">
        <TextInput
          name="search"
          type="text"
          label="Search"
          className="w-full [&>.relative>input]:pr-[42px] [&>.label]:p-0 [&>.label]:pb-1 bg-white"
          autoComplete="off"
          onChange={(e) => {
            searchBookings(e.target.value);
          }}
          value={searchTerm}
        />
        <SearchIcon className="absolute w-6 h-6 stroke-black right-[14px] bottom-[14px]" />
      </div>
      {loading && (
        <div className="flex justify-center items-center py-16">
          <div className="w-8 h-8 border-4 border-sportsBlue border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && filteredBookings.length === 0 && (
        <div className="flex flex-col gap-y-8 overflow-y-auto">
          <>
            <p className="text-[24px] leading-[34px] font-semibold text-[#101820]">
              Sorry we couldn&apos;t find any matches for: {searchTerm}
            </p>
            <p className="font-semibold text-[#909090]">
              Please try searching with another term
            </p>
          </>
        </div>
      )}
      <div className="flex flex-col gap-y-8">
        {sortedDates.map((date) => (
          <div key={date}>
            <p className="text-[12px] font-medium mb-[30px]">{date}</p>
            <div className="flex flex-col gap-y-3">
              {groupedBookings[date].map((booking, index) => (
                <BookingCard key={index} booking={booking} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {filterOverlayVisible && (
        <div className="absolute top-0 left-0 h-screen bg-white p-6 z-[999] max-w-[430px] w-full">
          <div className="flex flex-row justify-between items-center">
            <p className="font-semibold text-[24px]">Filters</p>
            <button onClick={toggleFilterOverlay}>
              <img src="/images/icons/close.svg" />
            </button>
          </div>
          <div className="flex flex-col gap-x-4">
            <DatePicker label="Date From" name="date-from" onChange={(e) => setDateFrom(e.target.value)} />
            <DatePicker label="Date To" name="date-to" onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {activityTypes.length > 0 && (
            <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-4 mt-4">
              <p className="text-sportsBlack font-medium text-base leading-[26px] mb-3">Activity</p>
              <div className="flex flex-row gap-3 flex-wrap">
                {activityTypes.map((a) => (
                  <div
                    key={a.BaseActivityId}
                    className={`flex items-center gap-1 p-2 px-3 rounded-full font-medium cursor-pointer border-[2px] border-transparent bg-sportsLightGrey ${
                      selectedActivity.includes(a.BaseActivityId) ? "!border-sportsBlue" : ""
                    }`}
                    onClick={() => handleActivityClick(a.BaseActivityId)}
                  >
                    <p className="text-[10px] capitalize">{a.BaseActivityName}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Button
              variant="primary"
              className="w-full mt-4"
              onClick={() => {
                setFilterOverlayVisible(false);
                filterBookings();
              }}
            >
              Update Search
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
